import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/schematics/ReceiverEventsModal.jsx
import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    useTheme,
    Tooltip,
    Stack,
    Tabs,
    Tab,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    IconButton,
    alpha
} from '@mui/material';
import {
    Close as CloseIcon,
    Power as PowerIcon,
    Refresh as RefreshIcon,
    WifiOff as WifiOffIcon,
    Wifi as WifiOnIcon,
    SyncAlt as SyncAltIcon,
    Send as SendIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as TimeIcon,
    Router as RouterIcon,
    KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { useReceiverEvents } from '../../hooks/useReceiverEvents';
import { api } from '../../services/api';

// Configuración visual por tipo de evento
const EVENT_TYPE_CONFIG = {
    'HB ONLINE': {
        label: i18n.t('hardcoded.components_vehicles_vehiclelist.online'),
        icon: WifiOnIcon,
        color: '#4caf50',
        bgColor: 'rgba(76, 175, 80, 0.07)',
    },
    'HB OFFLINE': {
        label: i18n.t('hardcoded.components_vehicles_vehiclelist.offline'),
        icon: WifiOffIcon,
        color: '#9e9e9e',
        bgColor: 'rgba(158, 158, 158, 0.07)',
    },
    'STARTUP': {
        label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.inicio'),
        icon: PowerIcon,
        color: '#2196f3',
        bgColor: 'rgba(33, 150, 243, 0.07)',
    },
    'POWEROFF': {
        label: i18n.t('hardcoded.components_schematics_receivereventsmodal.apagado'),
        icon: PowerIcon,
        color: '#ff9800',
        bgColor: 'rgba(255, 152, 0, 0.07)',
    },
    'PING': {
        label: 'Heartbeat',
        icon: SyncAltIcon,
        color: '#9c27b0',
        bgColor: 'rgba(156, 39, 176, 0.07)',
    },
    'WDT_RESET': {
        label: i18n.t('hardcoded.components_schematics_receivereventsmodal.reset'),
        icon: RefreshIcon,
        color: '#f44336',
        bgColor: 'rgba(244, 67, 54, 0.07)',
    },
    'RESP': {
        label: 'Respuesta',
        icon: CheckCircleIcon,
        color: '#00bcd4',
        bgColor: 'rgba(0, 188, 212, 0.07)',
    }
};

const STATUS_CONFIG = {
    'online': { label: i18n.t('hardcoded.components_vehicles_vehiclelist.online'), color: 'success' },
    'warning': { label: i18n.t('hardcoded.components_vehicles_vehiclelist.warning'), color: 'warning' },
    'offline': { label: i18n.t('hardcoded.components_vehicles_vehiclelist.offline'), color: 'default' },
    'sin_receptor': { label: i18n.t('hardcoded.components_schematics_receivereventsmodal.sin_receptor'), color: 'error' }
};

// ─── Selector de receptor (chips horizontales) ────────────────────────────────
const ReceiverSelector = ({ receptores, selectedIdx, onChange, label }) => {
  const { t } = useTranslation();
    if (!receptores || receptores.length <= 1) return null;
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {receptores.map((r, idx) => (
                    <Chip
                        key={r.id}
                        icon={<RouterIcon sx={{ fontSize: '0.9rem !important' }} />}
                        label={r.mac_address}
                        onClick={() => onChange(idx)}
                        color={selectedIdx === idx ? 'primary' : 'default'}
                        variant={selectedIdx === idx ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.73rem',
                            cursor: 'pointer',
                            fontWeight: selectedIdx === idx ? 700 : 400,
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );
};

// ─── Barra de info del receptor seleccionado ─────────────────────────────────
const ReceiverInfoBar = ({ receiver, status, lastOnline, formatTime, timeSince }) => {
  const { t } = useTranslation();
    if (!receiver) return null;
    const statusCfg = STATUS_CONFIG[status] || { label: t('hardcoded.components_schematics_receivereventsmodal.desconocido'), color: 'default' };
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, borderRadius: 1.5 }}>
            <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Box>
                    <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_schematics_receivereventsmodal.mac_receptor')}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                        {receiver.mac_address}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">ID DynamoDB</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                        {receiver.dynamo_receiver_id}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">{i18n.t('gps.table.status')}</Typography>
                    <Box sx={{ mt: 0.25 }}>
                        <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
                    </Box>
                </Box>
                {lastOnline && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">{t('hardcoded.components_schematics_receivereventsmodal.ultimo_online')}</Typography>
                        <Tooltip title={formatTime(lastOnline)} arrow>
                            <Typography variant="body2" fontWeight={500} sx={{ cursor: 'help', mt: 0.25 }}>
                                {timeSince(lastOnline)}
                            </Typography>
                        </Tooltip>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const ReceiverEventsModal = ({
    open,
    onClose,
    receptores = [],   // Array de receptores asignados al vehículo
}) => {
  const { t } = useTranslation();
    const theme = useTheme();

    // ── Todos los hooks arriba (antes de cualquier return) ──────────────────
    const [currentTab, setCurrentTab] = useState(0);
    const [eventsReceiverIdx, setEventsReceiverIdx] = useState(0);
    const [mqttReceiverIdx, setMqttReceiverIdx] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // MQTT
    const [selectedCommand, setSelectedCommand] = useState('VERSION');
    const [timeoutSeconds, setTimeoutSeconds] = useState(30);
    const [commandLoading, setCommandLoading] = useState(false);
    const [commandResult, setCommandResult] = useState(null);
    const [commandError, setCommandError] = useState(null);

    const { formatUserDateTimeSync } = useUserTimezone();

    // Calcular índices seguros antes del early return
    const safeEventsIdx = Math.min(eventsReceiverIdx, Math.max(0, receptores.length - 1));
    const safeMqttIdx   = Math.min(mqttReceiverIdx,   Math.max(0, receptores.length - 1));
    const eventsReceiverId = receptores[safeEventsIdx]?.id ?? null;
    const mqttReceiver     = receptores[safeMqttIdx] ?? null;

    // Hook de eventos — se auto-refetch cuando cambia el receptor o al abrir el modal
    const {
        loading: eventsLoading,
        error: eventsError,
        events,
        status,
        lastOnline,
        refetch: refetchEvents,
    } = useReceiverEvents(open ? eventsReceiverId : null);

    // Formateo de fecha
    const formatEventTime = useCallback((iso) => {
        if (!iso) return t('hardcoded.components_alarms_alarmlist.sin_datos');
        try {
            return formatUserDateTimeSync(iso, {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                timeZoneName: 'short'
            });
        } catch { return iso; }
    }, [formatUserDateTimeSync]);

    // Tiempo transcurrido
    const getTimeSince = useCallback((iso) => {
        if (!iso) return null;
        try {
            const diff = Date.now() - new Date(iso).getTime();
            const m = Math.floor(diff / 60000);
            const h = Math.floor(diff / 3600000);
            const d = Math.floor(diff / 86400000);
            if (m < 1)  return t('hardcoded.components_schematics_receivereventsmodal.hace_menos_de_1_min');
            if (m < 60) return `Hace ${m} min`;
            if (h < 24) return t('hardcoded.components_schematics_receivereventsmodal.hace_hh', { h: h });
            return t('hardcoded.components_schematics_receivereventsmodal.hace_dd', { d: d });
        } catch { return null; }
    }, []);

    // Reset al abrir/cerrar
    useEffect(() => {
        if (open) {
            setCurrentTab(0);
            setEventsReceiverIdx(0);
            setMqttReceiverIdx(0);
            setPage(0);
            setCommandResult(null);
            setCommandError(null);
        }
    }, [open]);

    // ── Early return (después de todos los hooks) ───────────────────────────
    if (!receptores || receptores.length === 0) return null;

    const hasMultiple = receptores.length > 1;
    const selectedEventsReceiver = receptores[safeEventsIdx];

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleTabChange = (_, v) => setCurrentTab(v);

    const handleEventsReceiverChange = (idx) => {
        setEventsReceiverIdx(idx);
        setPage(0);
    };

    const handleMqttReceiverChange = (idx) => {
        setMqttReceiverIdx(idx);
        setCommandResult(null);
        setCommandError(null);
    };

    const handleSendCommand = async () => {
        if (!mqttReceiver) return;
        setCommandLoading(true);
        setCommandError(null);
        setCommandResult(null);

        try {
            const response = await api.receivers.sendCommand(mqttReceiver.id, {
                command: selectedCommand,
                timeout_seconds: timeoutSeconds
            });

            console.log('📨 Command response:', response.data);
            setCommandResult(response.data);

            if (response.data.timeout) {
                setCommandError(t('hardcoded.components_schematics_receivereventsmodal.timeout_no_se_recibio_respuesta', { timeoutSeconds: timeoutSeconds }));
            } else if (!response.data.success) {
                setCommandError(response.data.error || t('hardcoded.components_schematics_receivereventsmodal.error_desconocido_al_procesar_el'));
            }
        } catch (error) {
            console.error(t('hardcoded.components_schematics_receivereventsmodal.error_sending_command'), error);
            setCommandError(error.response?.data?.detail || error.message || t('hardcoded.components_schematics_receivereventsmodal.error_al_enviar_el_comando'));
        } finally {
            setCommandLoading(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, minHeight: '72vh', display: 'flex', flexDirection: 'column' }
            }}
        >
            {/* ── HEADER ──────────────────────────────────────────────────── */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: 1.5,
                    pt: 2
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {t('hardcoded.components_schematics_receivereventsmodal.eventos_del_receptor')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {hasMultiple
                            ? t('hardcoded.components_schematics_receivereventsmodal.length_receptores_asignados_a_este', { length: receptores.length })
                            : `${receptores[0]?.mac_address}${receptores[0]?.vehicle_name ? ` · ${receptores[0].vehicle_name}` : ''}`
                        }
                    </Typography>
                </Box>
                {/* Un único botón de cierre — en el header */}
                <IconButton onClick={onClose} size="small" sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── TABS ────────────────────────────────────────────────────── */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 1 }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab label={t('hardcoded.components_schematics_receivereventsmodal.historial_de_eventos')} />
                    <Tab label="Comandos MQTT" />
                </Tabs>
            </Box>

            {/* ── CONTENIDO ───────────────────────────────────────────────── */}
            <DialogContent sx={{ pt: 3, flex: 1, minHeight: 400 }}>

                {/* ░░ TAB 0 — Historial de Eventos ░░░░░░░░░░░░░░░░░░░░░░ */}
                {currentTab === 0 && (
                    <Box>
                        {/* Selector de receptor */}
                        <ReceiverSelector
                            receptores={receptores}
                            selectedIdx={safeEventsIdx}
                            onChange={handleEventsReceiverChange}
                            label={t('hardcoded.components_schematics_receivereventsmodal.seleccionar_receptor')}
                        />

                        {/* Barra de info */}
                        <ReceiverInfoBar
                            receiver={selectedEventsReceiver}
                            status={status}
                            lastOnline={lastOnline}
                            formatTime={formatEventTime}
                            timeSince={getTimeSince}
                        />

                        {/* Cargando */}
                        {eventsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 2 }}>
                                <CircularProgress size={32} />
                                <Typography color="text.secondary">{i18n.t('hardcoded.components_schematics_receivereventsmodal.cargando_eventos')}</Typography>
                            </Box>
                        )}

                        {/* Error */}
                        {eventsError && !eventsLoading && (
                            <Alert severity="error" sx={{ mb: 2 }} action={
                                <Button color="inherit" size="small" onClick={refetchEvents}>{i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
                            }>
                                {t('hardcoded.components_schematics_receivereventsmodal.error_al_cargar_los_eventos')}{eventsError}
                            </Alert>
                        )}

                        {/* Sin eventos */}
                        {!eventsLoading && !eventsError && events.length === 0 && (
                            <Alert severity="info">
                                {t('hardcoded.components_schematics_receivereventsmodal.no_hay_eventos_registrados_para')}</Alert>
                        )}

                        {/* Tabla de eventos */}
                        {!eventsLoading && !eventsError && events.length > 0 && (
                            <>
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                                            <TableRow>
                                                <TableCell width="22%">
                                                    <Typography variant="subtitle2" fontWeight={700}>{i18n.t('vehicles.form.type')}</Typography>
                                                </TableCell>
                                                <TableCell width="38%">
                                                    <Typography variant="subtitle2" fontWeight={700}>{i18n.t('audit.table.description')}</Typography>
                                                </TableCell>
                                                <TableCell width="40%">
                                                    <Typography variant="subtitle2" fontWeight={700}>{i18n.t('hardcoded.components_schematics_receivereventsmodal.fecha_hora')}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {events
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((event, idx) => {
                                                    const cfg = EVENT_TYPE_CONFIG[event.event_type];
                                                    const IconComp = cfg?.icon || RefreshIcon;
                                                    return (
                                                        <TableRow
                                                            key={idx}
                                                            hover
                                                            sx={{
                                                                bgcolor: cfg?.bgColor,
                                                                '&:hover': { bgcolor: alpha(cfg?.bgColor || '#fff', 0.6) }
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                    <IconComp sx={{ color: cfg?.color, fontSize: '1.1rem', flexShrink: 0 }} />
                                                                    <Chip
                                                                        label={cfg?.label || event.event_type}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            borderColor: cfg?.color,
                                                                            color: cfg?.color,
                                                                            fontWeight: 700,
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {event.display_type}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                    {formatEventTime(event.timestamp)}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={events.length}
                                    page={page}
                                    onPageChange={(_, p) => setPage(p)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    labelRowsPerPage={t('hardcoded.components_schematics_receivereventsmodal.por_pagina')}
                                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                                />
                            </>
                        )}
                    </Box>
                )}

                {/* ░░ TAB 1 — Comandos MQTT ░░░░░░░░░░░░░░░░░░░░░░░░░░░░ */}
                {currentTab === 1 && (
                    <Box>
                        {/* Selector de receptor para MQTT */}
                        <ReceiverSelector
                            receptores={receptores}
                            selectedIdx={safeMqttIdx}
                            onChange={handleMqttReceiverChange}
                            label={t('hardcoded.components_schematics_receivereventsmodal.receptor_destino')}
                        />

                        {/* Info de tópicos */}
                        <Alert
                            severity="info"
                            sx={{ mb: 3, borderRadius: 1.5 }}
                            icon={<RouterIcon fontSize="inherit" />}
                        >
                            <Typography variant="body2" gutterBottom>
                                {t('hardcoded.components_schematics_receivereventsmodal.envia_comandos_al_receptor_para')}</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">{t('hardcoded.components_admin_ordermanagement.envio')}</Typography>
                                    <Chip
                                        label={`CMD/${mqttReceiver?.dynamo_receiver_id ?? '—'}`}
                                        size="small"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.1) }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_schematics_receivereventsmodal.respuesta')}</Typography>
                                    <Chip
                                        label={`TRX/${mqttReceiver?.dynamo_receiver_id ?? '—'}`}
                                        size="small"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: alpha(theme.palette.success.main, 0.1) }}
                                    />
                                </Box>
                            </Stack>
                        </Alert>

                        {/* Formulario de comando */}
                        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                {t('hardcoded.components_schematics_receivereventsmodal.enviar_comando')}</Typography>
                            <Divider sx={{ mb: 2.5 }} />
                            <Stack spacing={2.5}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{i18n.t('hardcoded.components_schematics_receivereventsmodal.comando')}</InputLabel>
                                    <Select
                                        value={selectedCommand}
                                        label={t('hardcoded.components_schematics_receivereventsmodal.comando')}
                                        onChange={(e) => setSelectedCommand(e.target.value)}
                                        disabled={commandLoading}
                                    >
                                        <MenuItem value="VERSION">{t('hardcoded.components_schematics_receivereventsmodal.version_informacion_del_firmware')}</MenuItem>
                                        <MenuItem value="STATUS">{t('hardcoded.components_schematics_receivereventsmodal.status_estado_del_receptor')}</MenuItem>
                                        <MenuItem value="PING">{t('hardcoded.components_schematics_receivereventsmodal.ping_verificar_conectividad')}</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Timeout (segundos)"
                                    value={timeoutSeconds}
                                    onChange={(e) => setTimeoutSeconds(Math.max(5, parseInt(e.target.value) || 30))}
                                    disabled={commandLoading}
                                    helperText={t('hardcoded.components_schematics_receivereventsmodal.tiempo_maximo_de_espera_para')}
                                    InputProps={{ endAdornment: <TimeIcon color="action" fontSize="small" /> }}
                                    inputProps={{ min: 5, max: 120 }}
                                />

                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={commandLoading
                                        ? <CircularProgress size={18} color="inherit" />
                                        : <SendIcon />
                                    }
                                    onClick={handleSendCommand}
                                    disabled={commandLoading || !mqttReceiver}
                                    sx={{ py: 1.25 }}
                                >
                                    {commandLoading
                                        ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {i18n.t('hardcoded.components_schematics_receivereventsmodal.esperando_respuesta')}<Typography variant="caption" sx={{ opacity: 0.75 }}>{t('hardcoded.components_schematics_receivereventsmodal.max')}{timeoutSeconds}s)</Typography>
                                          </Box>
                                        : t('hardcoded.components_schematics_receivereventsmodal.enviar_selectedcommand_mqttreceivermac', { selectedCommand: selectedCommand, mqttReceivermac: mqttReceiver?.mac_address ?? '—' })
                                    }
                                </Button>
                            </Stack>
                        </Paper>

                        {/* ── Estado: enviando ─────────────────────────── */}
                        {commandLoading && (
                            <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={16} color="inherit" />
                                    <Typography variant="body2">
                                        {t('hardcoded.components_schematics_receivereventsmodal.comando_enviado_esperando_respuesta_del')}<strong>{mqttReceiver?.mac_address}</strong>…
                                    </Typography>
                                </Stack>
                            </Alert>
                        )}

                        {/* ── Error / Timeout ───────────────────────────── */}
                        {!commandLoading && commandError && (
                            <Alert
                                severity={commandResult?.timeout ? 'warning' : 'error'}
                                sx={{ mb: 2, borderRadius: 1.5 }}
                            >
                                <Typography variant="body2" fontWeight={700} gutterBottom>
                                    {commandResult?.timeout ? t('hardcoded.components_schematics_receivereventsmodal.sin_respuesta_timeout') : t('hardcoded.components_schematics_receivereventsmodal.error_al_ejecutar_el_comando')}
                                </Typography>
                                <Typography variant="body2">{commandError}</Typography>
                            </Alert>
                        )}

                        {/* ── Respuesta exitosa ──────────────────────────── */}
                        {!commandLoading && commandResult && commandResult.success && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 0,
                                    borderRadius: 1.5,
                                    borderColor: 'success.main',
                                    borderWidth: 1.5,
                                    overflow: 'hidden'
                                }}
                            >
                                {/* Cabecera verde */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2.5,
                                    py: 1.5,
                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                    borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                                }}>
                                    <CheckCircleIcon color="success" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={700} color="success.main">
                                        {i18n.t('hardcoded.components_schematics_receivereventsmodal.respuesta_recibida')}</Typography>
                                </Box>

                                <Box sx={{ p: 2.5 }}>
                                    {/* Meta del comando */}
                                    <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_schematics_receivereventsmodal.comando')}</Typography>
                                            <Chip label={commandResult.command} size="small" color="primary" sx={{ ml: 0.75 }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Seq</Typography>
                                            <Chip label={commandResult.seq ?? '—'} size="small" sx={{ ml: 0.75 }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_admin_ordermanagement.enviado')}</Typography>
                                            <Typography variant="caption" sx={{ ml: 0.75 }}>
                                                {commandResult.sent_at
                                                    ? new Date(commandResult.sent_at).toLocaleString()
                                                    : '—'
                                                }
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.components_schematics_receivereventsmodal.receptor')}</Typography>
                                            <Typography variant="caption" fontFamily="monospace" sx={{ ml: 0.75 }}>
                                                {mqttReceiver?.mac_address ?? '—'}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Datos de respuesta — siempre visibles */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <ArrowDownIcon fontSize="small" color="action" />
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {t('hardcoded.components_schematics_receivereventsmodal.datos_de_la_respuesta')}</Typography>
                                    </Box>

                                    {commandResult.response && commandResult.response.length > 0 ? (
                                        <Box sx={{
                                            bgcolor: theme.palette.mode === 'dark'
                                                ? alpha('#000', 0.3)
                                                : alpha(theme.palette.grey[900], 0.04),
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            p: 2,
                                            fontFamily: 'monospace',
                                            fontSize: '0.82rem',
                                            overflowX: 'auto',
                                            maxHeight: 380,
                                            overflowY: 'auto',
                                        }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                                {JSON.stringify(commandResult.response, null, 2)}
                                            </pre>
                                        </Box>
                                    ) : (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            {t('hardcoded.components_schematics_receivereventsmodal.el_receptor_respondio_pero_no')}</Alert>
                                    )}
                                </Box>
                            </Paper>
                        )}
                    </Box>
                )}
            </DialogContent>

            {/* ── ACCIONES — Un único botón "Cerrar" ─────────────────────── */}
            <DialogActions
                sx={{
                    p: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between'
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {currentTab === 0 && !eventsLoading && events.length > 0 &&
                        t('hardcoded.components_schematics_receivereventsmodal.length_eventos_mac_address', { length: events.length, mac_address: selectedEventsReceiver?.mac_address })
                    }
                    {currentTab === 1 && mqttReceiver &&
                        t('hardcoded.components_schematics_receivereventsmodal.receptor_activo_mac_address', { mac_address: mqttReceiver.mac_address })
                    }
                </Typography>
                <Button onClick={onClose} variant="outlined" size="small">
                    {t('common.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReceiverEventsModal;
